import React, { useState, useEffect } from "react";
import { Box, Typography, FormControl, Select, MenuItem, Stack, Chip, Button, Snackbar, Alert, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ChevronRight } from "@mui/icons-material";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import useActivityLogData from "../hooks/useActivityLogData";

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACTION_STYLES = {
	create: { backgroundColor: "#D4EDDA", color: "#155724", fontSize: "0.85rem" },
	update: { backgroundColor: "#D1ECF1", color: "#0C5460", fontSize: "0.85rem" },
	delete: { backgroundColor: "#F8D7DA", color: "#721C24", fontSize: "0.85rem" },
	import: { backgroundColor: "#FFF3CD", color: "#856404", fontSize: "0.85rem" },
	suspend: { backgroundColor: "#FDECEA", color: "#611A15", fontSize: "0.85rem" },
	reactivate: { backgroundColor: "#E2E3E5", color: "#383D41", fontSize: "0.85rem" },
};

const BUTTON_SX = {
	backgroundColor: "#1A3F7A !important",
	color: "#EBF5FF",
	"&:hover": { backgroundColor: "#EBF5FF" },
};

// ─── AuditFilterTable (real data) ─────────────────────────────────────────────

const AuditFilterTable = ({ filterType, onFilterSelect, selectedFilter }) => {
	const { activityLogsFilters, isLoading } = useActivityLogData(0, 50);

	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 7 });

	const getRows = () => {
		if (!activityLogsFilters || !filterType) return [];

		const data = activityLogsFilters[filterType] || [];

		return data.map((item, index) => {
			if (filterType === "fullname") {
				return { id: index + 1, label: item.name, filterValue: item.user_id };
			}
			return { id: index + 1, label: item, filterValue: item };
		});
	};

	const headerLabels = { fullname: "Name", action: "Action", date: "Date" };

	const columns = [
		{
			field: "label",
			headerName: headerLabels[filterType] || "Filter",
			headerAlign: "center",
			flex: 1,
			minWidth: 150,
			align: "center",
		},
		{
			field: "action",
			headerName: "View",
			headerAlign: "center",
			width: 100,
			minWidth: 100,
			align: "center",
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Button
					variant={selectedFilter === params.row.filterValue ? "contained" : "outlined"}
					size="small"
					onClick={() => onFilterSelect(params.row.filterValue, params.row.label)}
					sx={{
						minWidth: 60,
						fontSize: "12px",
						...BUTTON_SX,
						borderColor: "#1A3F7A",
						...(selectedFilter !== params.row.filterValue && {
							backgroundColor: "transparent !important",
							color: "#1A3F7A",
							borderColor: "#1A3F7A",
							...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
						}),
					}}
				>
					<ChevronRight />
				</Button>
			),
		},
	];

	const rows = getRows();

	return (
		<Box sx={{ width: "100%" }}>
			<DataGrid
				rows={rows}
				columns={columns}
				rowHeight={60}
				disableColumnMenu
				disableRowSelectionOnClick
				showToolbar
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[7]}
				loading={isLoading}
				slotProps={{
					toolbar: {
						quickFilterProps: {
							sx: { "& input": { border: "none !important", boxShadow: "none !important", background: "transparent" } },
						},
						csvOptions: { disableToolbarButton: true },
						printOptions: { disableToolbarButton: true },
						filterButtonProps: { sx: { display: "none" } },
					},
					loadingOverlay: {
						variant: "linear-progress",
						noRowsVariant: "linear-progress",
					},
					pagination: { showFirstButton: false, showLastButton: false },
				}}
				sx={{
					"& .MuiDataGrid-cell": {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderLeft: "1px solid #E4E4E4",
						borderRight: "1px solid #E4E4E4",
						padding: "10px 16px",
					},
					"& .MuiDataGrid-columnHeader": { backgroundColor: "#F8F8F8", fontWeight: "bold", color: "#888888" },
					"& .MuiDataGrid-row:hover": { backgroundColor: "rgba(63,73,38,0.04)" },
					"& .MuiDataGrid-cellEmpty": { padding: 0, border: "none" },
					"& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon":
						{ display: "none" },
					"& .MuiDataGrid-footerContainer": { display: "flex", alignItems: "center", justifyContent: "center" },
					"& .MuiIconButton-root": { backgroundColor: "transparent !important" },
				}}
			/>
		</Box>
	);
};

// ─── AuditLogTable (real data) ─────────────────────────────────────────────────

const AuditLogTable = ({ filterType, filterValue, showAllColumns = false }) => {
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const { activityLogsData, isLoading, activityLogsErrors, totalRows } = useActivityLogData(
		paginationModel.page,
		paginationModel.pageSize,
		filterType,
		filterValue
	);

	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	useEffect(() => {
		if (activityLogsErrors) {
			setSnackbar({ open: true, message: activityLogsErrors, severity: "error" });
		}
	}, [activityLogsErrors]);

	const baseColumns = [
		{
			field: "action",
			headerName: "Action",
			headerAlign: "center",
			flex: 1,
			minWidth: 150,
			renderCell: (params) => {
				const key = (params.value || "").split("_")[0].toLowerCase();
				const styles = ACTION_STYLES[key] || { backgroundColor: "#E9ECEF", color: "#495057", fontSize: "0.85rem" };
				return <Chip label={key.charAt(0).toUpperCase() + key.slice(1)} sx={{ ...styles, fontWeight: 500, textTransform: "capitalize" }} />;
			},
		},
		{
			field: "user_name",
			headerName: "User",
			headerAlign: "center",
			flex: 1,
			minWidth: 150,
		},
		{
			field: "log",
			headerName: "Description",
			headerAlign: "center",
			flex: 4,
			minWidth: 300,
			renderCell: (params) => <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.2 }}>{params.value}</Box>,
		},
		{
			field: "created_at",
			headerName: "Date",
			headerAlign: "center",
			flex: 1,
			minWidth: 180,
		},
	];

	const columns = showAllColumns
		? baseColumns
		: baseColumns.filter((col) => {
				if (filterType === "fullname" && col.field === "user_name") return false;
				if (filterType === "action" && col.field === "action") return false;
				if (filterType === "date" && col.field === "created_at") return false;
				return true;
			});

	return (
		<Box sx={{ width: "100%" }}>
			<DataGrid
				rows={activityLogsData.data}
				rowCount={totalRows}
				columns={columns}
				rowHeight={80}
				disableColumnMenu
				disableRowSelectionOnClick
				showToolbar
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				paginationMode="server"
				pageSizeOptions={[10, 25, 50]}
				loading={isLoading}
				slotProps={{
					toolbar: {
						quickFilterProps: {
							sx: { "& input": { border: "none !important", boxShadow: "none !important", background: "transparent" } },
						},
						csvOptions: { disableToolbarButton: true },
						printOptions: { disableToolbarButton: true },
					},
					loadingOverlay: {
						variant: "linear-progress",
						noRowsVariant: "linear-progress",
					},
				}}
				sx={{
					"& .MuiDataGrid-cell": {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderLeft: "1px solid #E4E4E4",
						borderRight: "1px solid #E4E4E4",
						padding: "10px 24px",
					},
					"& .MuiDataGrid-columnHeader": { backgroundColor: "#F8F8F8", fontWeight: "bold", color: "#888888" },
					"& .MuiDataGrid-row:hover": { backgroundColor: "rgba(63,73,38,0.04)" },
					"& .MuiDataGrid-cellEmpty": { padding: 0, border: "none" },
					"& .MuiDataGrid-footerContainer": {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						"& p, & .MuiTypography-root": { margin: "0 !important", marginBlockStart: "0 !important", marginBlockEnd: "0 !important" },
					},
					"& .MuiIconButton-root": { backgroundColor: "transparent !important" },
				}}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ zIndex: 9999 }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

// ─── Main AuditLog Component ──────────────────────────────────────────────────

const AuditLog = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

	const [currentFilter, setCurrentFilter] = useState("fullname");
	const [selectedFilterValue, setSelectedFilterValue] = useState("");
	const [selectedName, setSelectedName] = useState("");

	const handleFilterChange = (e) => {
		setCurrentFilter(e.target.value);
		setSelectedFilterValue("");
		setSelectedName("");
	};

	const handleFilterSelect = (filterValue, label) => {
		setSelectedFilterValue(filterValue);
		setSelectedName(label);
	};

	return (
		<Box sx={{ mb: 3, width: "100% !important" }}>
			<Box
				sx={{
					display: "flex",
					flexDirection: isMobile ? "column" : "row",
					alignItems: isMobile ? "flex-start" : "center",
					gap: isMobile ? 1 : 2,
					mb: isMobile ? 2 : 3,
				}}
			>
				<Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: "bold" }}>
					Current Filter:
				</Typography>
				<FormControl
					size="small"
					sx={{
						minWidth: isMobile ? "100%" : 120,
						"& .MuiSelect-select": { padding: "5px 8px !important" },
					}}
				>
					<Select value={currentFilter} onChange={handleFilterChange}>
						<MenuItem value="all">All</MenuItem>
						<MenuItem value="fullname">Name</MenuItem>
						<MenuItem value="action">Action</MenuItem>
						<MenuItem value="date">Date</MenuItem>
					</Select>
				</FormControl>
			</Box>

			<Stack direction={isDesktop ? "row" : "column"} spacing={isMobile ? 2 : 3}>
				{currentFilter !== "all" && (
					<Box flex={isDesktop ? 3 : 1} sx={{ width: "100%" }}>
						<Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight="bold" sx={{ mb: isMobile ? 1.5 : 2 }}>
							Filter Options
						</Typography>
						<AuditFilterTable filterType={currentFilter} onFilterSelect={handleFilterSelect} selectedFilter={selectedFilterValue} />
					</Box>
				)}

				<Box flex={currentFilter === "all" ? 12 : isDesktop ? 9 : 1} sx={{ width: "100%" }}>
					<Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight="bold" sx={{ mb: isMobile ? 1.5 : 2 }}>
						Audit Log Results
						{selectedFilterValue && currentFilter !== "all" && (
							<Typography
								component="span"
								variant="body2"
								sx={{
									ml: 1,
									color: "text.secondary",
									display: isMobile ? "block" : "inline",
									mt: isMobile ? 0.5 : 0,
								}}
							>
								(Filtered by: {selectedName})
							</Typography>
						)}
					</Typography>

					{currentFilter === "all" ? (
						<AuditLogTable filterType="all" showAllColumns />
					) : selectedFilterValue ? (
						<AuditLogTable filterType={currentFilter} filterValue={selectedFilterValue} />
					) : (
						<Box
							sx={{
								height: isMobile ? 150 : 200,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								border: "1px solid #e0e0e0",
								borderRadius: 1,
								p: isMobile ? 2 : 3,
							}}
						>
							<Typography variant={isMobile ? "body2" : "body1"} color="text.secondary" sx={{ textAlign: "center" }}>
								<strong>💡 TIP</strong>: Select a filter option from the {isMobile ? "table above" : "left table"} to view audit logs
							</Typography>
						</Box>
					)}
				</Box>
			</Stack>
		</Box>
	);
};

export default AuditLog;
